import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getOperatorDashboard, seedData, updatePayment, updateEnquiryFollowup, getStudioMatches, expireReservation, autoExpireReservations } from '@/lib/api';
import {
  AlertTriangle, CreditCard, DoorOpen, ClipboardList,
  MessageSquare, Loader2, TrendingUp, ChevronRight,
  CalendarClock, Check, User, X, Eye, Building2, Users,
  ArrowRight, MapPin, Calendar, Thermometer, Wrench
} from 'lucide-react';
import { Button } from '@/components/ui/button';

const studioStatusDot = {
  occupied: 'bg-[#3A4035]',
  vacant: 'bg-[#D8DBD3]',
  reserved: 'bg-[#C4A44E]',
};

const payStatusBadge = {
  paid: 'bg-[#E8EDE5] text-[#3A4035]',
  pending: 'bg-[#FDFAF0] text-[#8A7230]',
  overdue: 'bg-[#FDF2F2] text-[#B33A3A]',
};

export default function OperatorDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [selectedStudio, setSelectedStudio] = useState(null);
  const [studioMatches, setStudioMatches] = useState([]);
  const [matchesLoading, setMatchesLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState({});
  const navigate = useNavigate();

  useEffect(() => { loadDashboard(); }, []);

  const loadDashboard = async () => {
    try {
      // Auto-expire on dashboard load
      try { await autoExpireReservations(); } catch (err) { console.error('Auto-expire error:', err); }
      setData(await getOperatorDashboard());
    }
    catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleSelectStudio = async (studio) => {
    if (selectedStudio?.studio_id === studio.studio_id) {
      setSelectedStudio(null); setStudioMatches([]); return;
    }
    setSelectedStudio(studio);
    setStudioMatches([]);
    if (studio.status === 'vacant' || studio.status === 'reserved') {
      setMatchesLoading(true);
      try { setStudioMatches(await getStudioMatches(studio.studio_id)); }
      catch (err) { console.error(err); }
      finally { setMatchesLoading(false); }
    }
  };

  const handleSeed = async () => {
    setSeeding(true);
    try { await seedData(); await loadDashboard(); }
    catch (err) { console.error(err); }
    finally { setSeeding(false); }
  };

  const handleMarkPaid = async (paymentId) => {
    setActionLoading(prev => ({ ...prev, [paymentId]: true }));
    try {
      const now = new Date().toISOString().split('T')[0];
      await updatePayment(paymentId, { status: 'paid', paid_date: now, reference: `MANUAL-${Date.now().toString(36).toUpperCase()}` });
      await loadDashboard();
    } catch (err) { console.error(err); }
    finally { setActionLoading(prev => ({ ...prev, [paymentId]: false })); }
  };

  const handleCompleteFollowup = async (enquiryId) => {
    setActionLoading(prev => ({ ...prev, [enquiryId]: true }));
    try {
      await updateEnquiryFollowup(enquiryId, { follow_up_required: false, follow_up_date: '' });
      await loadDashboard();
    } catch (err) { console.error(err); }
    finally { setActionLoading(prev => ({ ...prev, [enquiryId]: false })); }
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 text-[#3A4035] animate-spin" /></div>;

  const s = data?.stats || {};
  const fin = data?.financials || {};
  const studios = data?.all_studios || [];
  const sites = data?.all_sites || [];
  const overdue = data?.overdue_payments || [];
  const followups = data?.followups_list || [];
  const expiringRes = data?.expiring_reservations || [];
  const sc = data?.status_counts || {};

  const handleExpireReservation = async (resId) => {
    setActionLoading(prev => ({ ...prev, [resId]: true }));
    try { await expireReservation(resId); await loadDashboard(); }
    catch (err) { console.error(err); }
    finally { setActionLoading(prev => ({ ...prev, [resId]: false })); }
  };

  return (
    <div className="animate-fade-in" data-testid="operator-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-medium text-[#1A1A1A] tracking-tight">Dashboard</h1>
          <p className="text-sm text-[#5C5C55] mt-1">What needs your attention today</p>
        </div>
        {s.studios_total === 0 && (
          <Button onClick={handleSeed} disabled={seeding}
            className="bg-[#3A4035] text-white rounded-full px-5 py-2.5 text-sm font-medium hover:bg-[#2B3027]"
            data-testid="seed-data-button">
            {seeding ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}Load Demo Data
          </Button>
        )}
      </div>

      {/* ── OVERDUE PAYMENTS (ACTIONABLE) ── */}
      {overdue.length > 0 && (
        <section className="mb-6" data-testid="overdue-section">
          <p className="text-xs tracking-[0.1em] uppercase font-semibold text-[#B33A3A] mb-3 flex items-center gap-1.5">
            <AlertTriangle className="w-3.5 h-3.5" strokeWidth={1.5} />
            Overdue Payments ({overdue.length})
          </p>
          <div className="space-y-2">
            {overdue.map(pay => (
              <div key={pay.payment_id}
                className="bg-[#FDF2F2] rounded-2xl border border-[#F0D4D4] p-4 flex items-center gap-3"
                data-testid={`overdue-card-${pay.payment_id}`}>
                <div className="w-9 h-9 rounded-full bg-white/60 flex items-center justify-center text-xs font-semibold text-[#B33A3A] shrink-0">
                  {(pay.occupier_name || '?').charAt(0)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[#1A1A1A]">{pay.occupier_name || pay.occupier_id}</p>
                  <p className="text-[11px] text-[#8A6060]">£{pay.amount} · Due {pay.due_date}{pay.business_name ? ` · ${pay.business_name}` : ''}</p>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <button onClick={() => handleMarkPaid(pay.payment_id)}
                    disabled={actionLoading[pay.payment_id]}
                    className="text-[11px] px-3 py-1.5 rounded-full font-medium bg-white text-[#3A4035] border border-[#E5E2DC] hover:bg-[#E8EDE5] transition-colors flex items-center gap-1"
                    data-testid={`overdue-markpaid-${pay.payment_id}`}>
                    {actionLoading[pay.payment_id] ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" strokeWidth={2} />}
                    Mark paid
                  </button>
                  <button onClick={() => navigate('/payments?status=overdue')}
                    className="text-[11px] px-3 py-1.5 rounded-full font-medium bg-white/60 text-[#8A6060] hover:bg-white transition-colors flex items-center gap-1"
                    data-testid={`overdue-view-${pay.payment_id}`}>
                    <Eye className="w-3 h-3" strokeWidth={1.5} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ── FOLLOW-UPS DUE TODAY (ACTIONABLE) ── */}
      {followups.length > 0 && (
        <section className="mb-6" data-testid="followups-section">
          <p className="text-xs tracking-[0.1em] uppercase font-semibold text-[#8A7230] mb-3 flex items-center gap-1.5">
            <CalendarClock className="w-3.5 h-3.5" strokeWidth={1.5} />
            Follow-ups Due ({followups.length})
          </p>
          <div className="space-y-2">
            {followups.map(enq => (
              <div key={enq.enquiry_id}
                className="bg-[#FDFAF0] rounded-2xl border border-[#EDE4C8] p-4 flex items-center gap-3"
                data-testid={`followup-card-${enq.enquiry_id}`}>
                <div className="w-9 h-9 rounded-full bg-white/60 flex items-center justify-center text-xs font-semibold text-[#8A7230] shrink-0">
                  {enq.name?.charAt(0)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[#1A1A1A]">{enq.name}</p>
                  <p className="text-[11px] text-[#8A7230]">{enq.business_type} · {enq.status?.replace('_', ' ')}{enq.follow_up_date ? ` · Due ${enq.follow_up_date}` : ''}</p>
                  {enq.notes?.length > 0 && (
                    <p className="text-[11px] text-[#8A8B85] italic truncate mt-0.5">{enq.notes[enq.notes.length - 1].text}</p>
                  )}
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <button onClick={() => handleCompleteFollowup(enq.enquiry_id)}
                    disabled={actionLoading[enq.enquiry_id]}
                    className="text-[11px] px-3 py-1.5 rounded-full font-medium bg-white text-[#3A4035] border border-[#E5E2DC] hover:bg-[#E8EDE5] transition-colors flex items-center gap-1"
                    data-testid={`followup-complete-${enq.enquiry_id}`}>
                    {actionLoading[enq.enquiry_id] ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" strokeWidth={2} />}
                    Done
                  </button>
                  <button onClick={() => navigate('/enquiries?view=followups')}
                    className="text-[11px] px-3 py-1.5 rounded-full font-medium bg-white/60 text-[#8A7230] hover:bg-white transition-colors"
                    data-testid={`followup-open-${enq.enquiry_id}`}>
                    <Eye className="w-3 h-3" strokeWidth={1.5} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ── EXPIRING RESERVATIONS ── */}
      {expiringRes.length > 0 && (
        <section className="mb-6" data-testid="expiring-reservations-section">
          <p className="text-xs tracking-[0.1em] uppercase font-semibold text-[#8A7230] mb-3 flex items-center gap-1.5">
            <DoorOpen className="w-3.5 h-3.5" strokeWidth={1.5} />
            Reservations Expiring ({expiringRes.length})
          </p>
          <div className="space-y-2">
            {expiringRes.map(res => {
              const isExpired = res.expiry_date < new Date().toISOString().split('T')[0];
              return (
                <div key={res.reservation_id} className={`rounded-2xl border p-4 flex items-center gap-3 ${isExpired ? 'bg-[#FDF2F2] border-[#F0D4D4]' : 'bg-[#FDFAF0] border-[#EDE4C8]'}`} data-testid={`expiring-res-${res.reservation_id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[#1A1A1A]">{res.enquiry_name || 'Unknown'}</p>
                    <p className="text-[11px] text-[#8A8B85]">{res.studio_name} · {isExpired ? 'Expired' : 'Expires'} {res.expiry_date}</p>
                  </div>
                  <button onClick={() => handleExpireReservation(res.reservation_id)} disabled={actionLoading[res.reservation_id]}
                    className={`text-[11px] px-3 py-1.5 rounded-full font-medium transition-colors flex items-center gap-1 ${isExpired ? 'bg-white text-[#B33A3A] border border-[#F0D4D4] hover:bg-[#FDF2F2]' : 'bg-white text-[#8A7230] border border-[#EDE4C8] hover:bg-[#FDFAF0]'}`}
                    data-testid={`expire-res-${res.reservation_id}`}>
                    {actionLoading[res.reservation_id] ? <Loader2 className="w-3 h-3 animate-spin" /> : <X className="w-3 h-3" strokeWidth={1.5} />}
                    Release
                  </button>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* ── OTHER PRIORITIES (tickets, vacant, new enquiries) ── */}
      {(() => {
        const others = [
          s.tickets_open > 0 && {
            level: 'critical', color: 'border-l-[#B33A3A]', bg: 'bg-[#FDF2F2]',
            icon: MessageSquare, iconColor: 'text-[#B33A3A]',
            title: `${s.tickets_open} open ticket${s.tickets_open > 1 ? 's' : ''}`,
            subtitle: 'Occupiers awaiting response',
            action: () => navigate('/tickets'),
          },
          s.studios_vacant > 0 && {
            level: 'attention', color: 'border-l-[#C4A44E]', bg: 'bg-[#FDFAF0]',
            icon: DoorOpen, iconColor: 'text-[#8A7230]',
            title: `${s.studios_vacant} vacant studio${s.studios_vacant > 1 ? 's' : ''}`,
            subtitle: 'Available for new occupiers',
            action: () => navigate('/studios'),
          },
          s.enquiries_new > 0 && {
            level: 'opportunity', color: 'border-l-[#3A4035]', bg: 'bg-[#F0F2EF]',
            icon: ClipboardList, iconColor: 'text-[#3A4035]',
            title: `${s.enquiries_new} new enquir${s.enquiries_new > 1 ? 'ies' : 'y'}`,
            subtitle: 'New leads to follow up',
            action: () => navigate('/enquiries?filter=new'),
          },
        ].filter(Boolean);
        if (others.length === 0 && overdue.length === 0 && followups.length === 0) {
          return (
            <section className="mb-6">
              <div className="bg-[#F0F2EF] rounded-2xl p-5 flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-[#E8EDE5] flex items-center justify-center">
                  <TrendingUp className="w-4 h-4 text-[#3A4035]" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-sm font-medium text-[#3A4035]">All clear</p>
                  <p className="text-xs text-[#5C5C55]">No urgent items. Operations running smoothly.</p>
                </div>
              </div>
            </section>
          );
        }
        return others.length > 0 && (
          <section className="mb-6 space-y-2" data-testid="other-priorities">
            {others.map((p, i) => (
              <button key={p.level} onClick={p.action}
                className={`w-full flex items-center gap-4 p-4 rounded-2xl border-l-4 ${p.color} ${p.bg} text-left hover:shadow-[0_4px_16px_rgba(0,0,0,0.06)] transition-all duration-200 active:scale-[0.99]`}
                data-testid={`priority-${p.level}-${i}`}>
                <p.icon className={`w-5 h-5 ${p.iconColor} shrink-0`} strokeWidth={1.5} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[#1A1A1A]">{p.title}</p>
                  <p className="text-xs text-[#5C5C55]">{p.subtitle}</p>
                </div>
                <ChevronRight className="w-4 h-4 text-[#8A8B85] shrink-0" strokeWidth={1.5} />
              </button>
            ))}
          </section>
        );
      })()}

      {/* ── PIPELINE SUMMARY COUNTS ── */}
      <section className="mb-6" data-testid="pipeline-summary">
        <p className="text-xs tracking-[0.1em] uppercase font-semibold text-[#5C5C55] mb-3">Pipeline</p>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          {[
            { label: 'New', count: sc.new || 0, filter: 'new', bg: 'bg-[#F4EFE6]', text: 'text-[#7A6849]' },
            { label: 'Contacted', count: sc.contacted || 0, filter: 'contacted', bg: 'bg-[#E8EDE5]', text: 'text-[#3A4035]' },
            { label: 'Viewing', count: sc.viewing_booked || 0, filter: 'viewing_booked', bg: 'bg-[#E0E6DE]', text: 'text-[#3A4035]' },
            { label: 'Reserved', count: sc.reserved || 0, filter: 'reserved', bg: 'bg-[#3A4035]', text: 'text-white' },
            { label: 'Signed', count: data?.pipeline_counts?.signed || 0, filter: 'signed', bg: 'bg-[#2B3027]', text: 'text-white' },
          ].map(item => (
            <button key={item.label} onClick={() => navigate(`/enquiries?stage=${item.filter}`)}
              className={`${item.bg} rounded-2xl p-4 text-left hover:shadow-sm transition-shadow active:scale-[0.98]`}
              data-testid={`pipeline-count-${item.filter}`}>
              <p className={`text-2xl font-heading font-medium ${item.text}`}>{item.count}</p>
              <p className={`text-xs font-medium ${item.text} opacity-70`}>{item.label}</p>
            </button>
          ))}
        </div>
      </section>

      {/* ── CONVERSION METRICS ── */}
      {(data?.conversion?.viewings_this_week > 0 || data?.conversion?.reservations_this_week > 0) && (
        <section className="mb-6" data-testid="conversion-section">
          <p className="text-xs tracking-[0.1em] uppercase font-semibold text-[#5C5C55] mb-3">This Week</p>
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-white rounded-2xl border border-[#E5E2DC] p-4 text-center">
              <p className="text-2xl font-heading font-medium text-[#1A1A1A]">{data.conversion.viewings_this_week}</p>
              <p className="text-[10px] text-[#8A8B85] mt-0.5">Viewings</p>
            </div>
            <div className="bg-[#F0F2EF] rounded-2xl border border-[#D8DBD3] p-4 text-center">
              <p className="text-2xl font-heading font-medium text-[#3A4035]">{data.conversion.reservations_this_week}</p>
              <p className="text-[10px] text-[#3A4035] mt-0.5">Reservations</p>
            </div>
            <div className={`rounded-2xl border p-4 text-center ${data.conversion.conversion_pct >= 50 ? 'bg-[#E8EDE5] border-[#D0D9CB]' : 'bg-white border-[#E5E2DC]'}`}>
              <p className={`text-2xl font-heading font-medium ${data.conversion.conversion_pct >= 50 ? 'text-[#3A4035]' : 'text-[#1A1A1A]'}`}>{data.conversion.conversion_pct}%</p>
              <p className="text-[10px] text-[#8A8B85] mt-0.5">Conversion</p>
            </div>
          </div>
        </section>
      )}

      {/* ── FINANCIAL VISIBILITY ── */}
      <section className="mb-6" data-testid="financials-section">
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs tracking-[0.1em] uppercase font-semibold text-[#5C5C55]">Financials This Month</p>
          <button onClick={() => navigate('/payments')} className="text-xs text-[#5C5C55] hover:text-[#1A1A1A] font-medium flex items-center gap-1" data-testid="view-all-payments-link">
            All payments <ChevronRight className="w-3 h-3" strokeWidth={1.5} />
          </button>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-white rounded-2xl border border-[#E5E2DC] p-5">
            <p className="text-xs text-[#8A8B85] mb-1">Total Due</p>
            <p className="text-xl font-heading font-medium text-[#1A1A1A]">£{fin.total_due?.toLocaleString() || '0'}</p>
          </div>
          <button onClick={() => navigate('/payments?status=paid')}
            className="bg-[#F0F2EF] rounded-2xl border border-[#E8EDE5] p-5 text-left hover:shadow-sm transition-shadow"
            data-testid="financial-collected">
            <p className="text-xs text-[#3A4035] mb-1">Collected</p>
            <p className="text-xl font-heading font-medium text-[#3A4035]">£{fin.total_collected?.toLocaleString() || '0'}</p>
          </button>
          <button onClick={() => navigate('/payments?status=overdue')}
            className={`rounded-2xl border p-5 text-left hover:shadow-sm transition-shadow ${fin.outstanding > 0 ? 'bg-[#FDF2F2] border-[#F0D4D4]' : 'bg-white border-[#E5E2DC]'}`}
            data-testid="financial-outstanding">
            <p className={`text-xs mb-1 ${fin.outstanding > 0 ? 'text-[#B33A3A]' : 'text-[#8A8B85]'}`}>Outstanding</p>
            <p className={`text-xl font-heading font-medium ${fin.outstanding > 0 ? 'text-[#B33A3A]' : 'text-[#1A1A1A]'}`}>£{fin.outstanding?.toLocaleString() || '0'}</p>
          </button>
        </div>
      </section>

      {/* ── STUDIOS OVERVIEW WITH DETAIL PANEL ── */}
      <section className="mb-6" data-testid="studios-overview-section">
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs tracking-[0.1em] uppercase font-semibold text-[#5C5C55]">Studios Overview</p>
          <button onClick={() => navigate('/studios')} className="text-xs text-[#5C5C55] hover:text-[#1A1A1A] font-medium flex items-center gap-1" data-testid="view-all-studios-link">
            All studios <ChevronRight className="w-3 h-3" strokeWidth={1.5} />
          </button>
        </div>
        <div className="flex items-center gap-4 mb-3">
          <span className="flex items-center gap-1.5 text-xs text-[#5C5C55]"><span className="w-2 h-2 rounded-full bg-[#3A4035]" />Occupied</span>
          <span className="flex items-center gap-1.5 text-xs text-[#5C5C55]"><span className="w-2 h-2 rounded-full bg-[#C4A44E]" />Reserved</span>
          <span className="flex items-center gap-1.5 text-xs text-[#5C5C55]"><span className="w-2 h-2 rounded-full bg-[#D8DBD3]" />Vacant</span>
        </div>

        <div className="bg-white rounded-2xl border border-[#E5E2DC] overflow-hidden">
          <div className="p-5 space-y-5">
            {sites.map(site => {
              const siteStudios = studios.filter(st => st.site_id === site.site_id);
              return (
                <div key={site.site_id}>
                  <p className="text-xs font-semibold text-[#5C5C55] mb-2.5">{site.name}</p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                    {siteStudios.map(studio => (
                      <button key={studio.studio_id}
                        onClick={() => handleSelectStudio(studio)}
                        className={`flex items-center gap-2.5 px-3 py-2.5 rounded-xl border transition-all text-left ${selectedStudio?.studio_id === studio.studio_id ? 'border-[#3A4035] bg-[#FAF9F6] shadow-sm' : 'border-[#F2F0EB] hover:border-[#E5E2DC] hover:bg-[#FAF9F6]'}`}
                        data-testid={`studio-mini-${studio.studio_id}`}>
                        <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${studioStatusDot[studio.status] || 'bg-[#D8DBD3]'}`} />
                        <div className="min-w-0">
                          <p className="text-xs font-medium text-[#1A1A1A] truncate">{studio.name?.replace('Studio ', 'S').replace(' - ', ' · ')}</p>
                          {studio.occupier_name ? (
                            <p className="text-[10px] text-[#8A8B85] truncate">{studio.occupier_name}</p>
                          ) : (
                            <p className="text-[10px] text-[#8A8B85] capitalize">{studio.status}</p>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          {/* ── STUDIO DETAIL PANEL ── */}
          {selectedStudio && (
            <div className="border-t border-[#E5E2DC] p-5 bg-[#FAF9F6] animate-fade-in" data-testid="studio-detail-panel">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="font-heading text-base font-medium text-[#1A1A1A]">{selectedStudio.name}</h3>
                  <p className="text-xs text-[#8A8B85]">{selectedStudio.description} · {selectedStudio.size_sqft} sq ft</p>
                </div>
                <button onClick={() => setSelectedStudio(null)} className="p-1 hover:bg-[#F2F0EB] rounded-lg" data-testid="close-studio-panel">
                  <X className="w-4 h-4 text-[#8A8B85]" strokeWidth={1.5} />
                </button>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                <div>
                  <p className="text-[10px] text-[#8A8B85] uppercase tracking-wider">Status</p>
                  <p className={`text-xs font-semibold capitalize mt-0.5 ${selectedStudio.status === 'occupied' ? 'text-[#3A4035]' : selectedStudio.status === 'reserved' ? 'text-[#8A7230]' : 'text-[#5C5C55]'}`}>{selectedStudio.status}</p>
                </div>
                <div>
                  <p className="text-[10px] text-[#8A8B85] uppercase tracking-wider">Rent</p>
                  <p className="text-xs font-semibold text-[#1A1A1A] mt-0.5">£{selectedStudio.monthly_rent}/mo</p>
                </div>
                {selectedStudio.occupier_name && (
                  <div>
                    <p className="text-[10px] text-[#8A8B85] uppercase tracking-wider">Occupier</p>
                    <p className="text-xs font-semibold text-[#1A1A1A] mt-0.5">{selectedStudio.occupier_name}</p>
                  </div>
                )}
                {selectedStudio.latest_payment && (
                  <div>
                    <p className="text-[10px] text-[#8A8B85] uppercase tracking-wider">Payment</p>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold capitalize mt-0.5 inline-block ${payStatusBadge[selectedStudio.latest_payment.status] || ''}`}>
                      {selectedStudio.latest_payment.status} · £{selectedStudio.latest_payment.amount}
                    </span>
                  </div>
                )}
              </div>

              {/* Quick actions */}
              <div className="flex flex-wrap gap-2">
                {selectedStudio.occupier_id && (
                  <button onClick={() => navigate('/occupiers')}
                    className="text-[11px] px-3 py-1.5 rounded-full font-medium bg-white text-[#3A4035] border border-[#E5E2DC] hover:bg-[#E8EDE5] transition-colors flex items-center gap-1.5"
                    data-testid="studio-action-view-occupier">
                    <User className="w-3 h-3" strokeWidth={1.5} /> View occupier
                  </button>
                )}
                {selectedStudio.occupier_id && (
                  <button onClick={() => navigate(`/payments?status=all`)}
                    className="text-[11px] px-3 py-1.5 rounded-full font-medium bg-white text-[#3A4035] border border-[#E5E2DC] hover:bg-[#E8EDE5] transition-colors flex items-center gap-1.5"
                    data-testid="studio-action-payments">
                    <CreditCard className="w-3 h-3" strokeWidth={1.5} /> Payments
                  </button>
                )}
                {!selectedStudio.occupier_id && (
                  <button onClick={() => navigate('/enquiries')}
                    className="text-[11px] px-3 py-1.5 rounded-full font-medium bg-[#3A4035] text-white hover:bg-[#2B3027] transition-colors flex items-center gap-1.5"
                    data-testid="studio-action-enquiries">
                    <ClipboardList className="w-3 h-3" strokeWidth={1.5} /> View enquiries
                  </button>
                )}
                <button onClick={() => navigate(`/studios?site=${selectedStudio.site_id}`)}
                  className="text-[11px] px-3 py-1.5 rounded-full font-medium bg-white text-[#5C5C55] border border-[#E5E2DC] hover:bg-[#F2F0EB] transition-colors flex items-center gap-1.5"
                  data-testid="studio-action-site">
                  <Building2 className="w-3 h-3" strokeWidth={1.5} /> Site studios
                </button>
              </div>

              {/* ── POTENTIAL MATCHES (vacant/reserved studios) ── */}
              {(selectedStudio.status === 'vacant' || selectedStudio.status === 'reserved') && (
                <div className="mt-4 pt-4 border-t border-[#E5E2DC]" data-testid="potential-matches">
                  <p className="text-[10px] tracking-[0.1em] uppercase font-semibold text-[#5C5C55] mb-3">Potential Matches</p>
                  {matchesLoading ? (
                    <div className="flex items-center gap-2 py-3"><Loader2 className="w-4 h-4 text-[#8A8B85] animate-spin" /><span className="text-xs text-[#8A8B85]">Finding matches...</span></div>
                  ) : studioMatches.length > 0 ? (
                    <div className="space-y-2">
                      {studioMatches.slice(0, 5).map(match => {
                        const mt = { cold: 'bg-[#E8EDF2] text-[#4A6080]', warm: 'bg-[#FDFAF0] text-[#8A7230]', hot: 'bg-[#FDF2F2] text-[#B33A3A]' }[match.lead_temperature] || 'bg-[#FDFAF0] text-[#8A7230]';
                        const latestNote = (match.notes || []).length > 0 ? match.notes[match.notes.length - 1] : null;
                        return (
                          <div key={match.enquiry_id} className="bg-white rounded-xl border border-[#E5E2DC] p-3" data-testid={`match-${match.enquiry_id}`}>
                            <div className="flex items-start gap-2">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-1.5 flex-wrap">
                                  <p className="text-xs font-medium text-[#1A1A1A]">{match.name}</p>
                                  <span className={`text-[8px] px-1.5 py-0.5 rounded-full font-bold uppercase ${mt}`}>{match.lead_temperature || 'warm'}</span>
                                  {match.follow_up_required && <CalendarClock className="w-3 h-3 text-[#C4A44E]" strokeWidth={1.5} />}
                                </div>
                                <p className="text-[10px] text-[#8A8B85] mt-0.5">{match.business_type}{match.preferred_location ? ` · ${match.preferred_location}` : ''}</p>
                                <div className="flex flex-wrap gap-1 mt-1">
                                  {(match.tags || []).slice(0, 3).map(t => (
                                    <span key={t} className="text-[8px] px-1.5 py-0.5 rounded-full bg-[#F2F0EB] text-[#5C5C55] font-medium">{t.replace(/_/g, ' ')}</span>
                                  ))}
                                </div>
                                {latestNote && <p className="text-[10px] text-[#8A8B85] italic mt-1 truncate">{latestNote.text}</p>}
                              </div>
                              <div className="flex flex-col gap-1 shrink-0">
                                <button onClick={() => navigate('/enquiries')}
                                  className="text-[10px] px-2.5 py-1 rounded-full font-medium bg-[#3A4035] text-white hover:bg-[#2B3027] transition-colors"
                                  data-testid={`match-contact-${match.enquiry_id}`}>
                                  Contact
                                </button>
                                <button onClick={() => navigate('/enquiries')}
                                  className="text-[10px] px-2.5 py-1 rounded-full font-medium bg-[#E8EDE5] text-[#3A4035] hover:bg-[#D8DBD3] transition-colors"
                                  data-testid={`match-viewing-${match.enquiry_id}`}>
                                  Book viewing
                                </button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-xs text-[#8A8B85] py-2">No matching enquiries found for this studio.</p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* ── BOTTOM ROW: Tickets + Quick Stats ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent tickets */}
        <section className="bg-white rounded-2xl border border-[#E5E2DC] shadow-[0_2px_10px_rgba(0,0,0,0.02)] p-6" data-testid="recent-tickets-section">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-base font-medium text-[#1A1A1A]">Support Tickets</h2>
            <button onClick={() => navigate('/tickets')} className="text-xs text-[#5C5C55] hover:text-[#1A1A1A] font-medium flex items-center gap-1" data-testid="view-all-tickets-link">
              View all <ChevronRight className="w-3 h-3" strokeWidth={1.5} />
            </button>
          </div>
          <div className="space-y-1">
            {(data?.recent_tickets || []).map(tkt => (
              <button key={tkt.ticket_id} onClick={() => navigate('/tickets')}
                className="w-full flex items-center gap-3 py-3 border-b border-[#F2F0EB] last:border-0 text-left hover:bg-[#FAF9F6] -mx-2 px-2 rounded-lg transition-colors"
                data-testid={`recent-ticket-${tkt.ticket_id}`}>
                <div className={`w-2 h-2 rounded-full shrink-0 ${tkt.status === 'open' ? 'bg-[#B33A3A]' : tkt.status === 'in_progress' ? 'bg-[#C4A44E]' : 'bg-[#3A4035]'}`} />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-[#1A1A1A] truncate">{tkt.subject}</p>
                  <p className="text-[10px] text-[#8A8B85]">{tkt.reporter_name} — {tkt.category}</p>
                </div>
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold shrink-0 ${tkt.status === 'open' ? 'bg-[#FDF2F2] text-[#B33A3A]' : tkt.status === 'in_progress' ? 'bg-[#FDFAF0] text-[#8A7230]' : 'bg-[#E8EDE5] text-[#3A4035]'}`}>
                  {tkt.status?.replace('_', ' ')}
                </span>
              </button>
            ))}
            {(data?.recent_tickets || []).length === 0 && <p className="text-xs text-[#8A8B85] py-6 text-center">No tickets</p>}
          </div>
        </section>

        {/* Quick Stats */}
        <section className="bg-white rounded-2xl border border-[#E5E2DC] shadow-[0_2px_10px_rgba(0,0,0,0.02)] p-6" data-testid="quick-stats-section">
          <h2 className="font-heading text-base font-medium text-[#1A1A1A] mb-5">Portfolio</h2>
          <div className="grid grid-cols-3 gap-4 mb-5">
            <button onClick={() => navigate('/sites')} className="text-center hover:bg-[#FAF9F6] rounded-xl py-3 transition-colors" data-testid="quickstat-sites">
              <p className="text-2xl font-heading font-medium text-[#1A1A1A]">{s.sites}</p>
              <p className="text-[10px] text-[#8A8B85] mt-0.5">Sites</p>
            </button>
            <button onClick={() => navigate('/studios')} className="text-center hover:bg-[#FAF9F6] rounded-xl py-3 transition-colors" data-testid="quickstat-studios">
              <p className="text-2xl font-heading font-medium text-[#1A1A1A]">{s.studios_total}</p>
              <p className="text-[10px] text-[#8A8B85] mt-0.5">Studios</p>
            </button>
            <button onClick={() => navigate('/occupiers')} className="text-center hover:bg-[#FAF9F6] rounded-xl py-3 transition-colors" data-testid="quickstat-occupiers">
              <p className="text-2xl font-heading font-medium text-[#1A1A1A]">{s.total_occupiers}</p>
              <p className="text-[10px] text-[#8A8B85] mt-0.5">Occupiers</p>
            </button>
          </div>
          <div className="pt-4 border-t border-[#F2F0EB] grid grid-cols-3 gap-3">
            <div className="bg-[#E8EDE5] rounded-xl p-3 text-center">
              <p className="text-lg font-heading font-medium text-[#3A4035]">{s.studios_occupied}</p>
              <p className="text-[10px] text-[#3A4035]">Occupied</p>
            </div>
            <div className="bg-[#FDFAF0] rounded-xl p-3 text-center">
              <p className="text-lg font-heading font-medium text-[#8A7230]">{s.studios_reserved}</p>
              <p className="text-[10px] text-[#8A7230]">Reserved</p>
            </div>
            <div className="bg-[#F2F0EB] rounded-xl p-3 text-center">
              <p className="text-lg font-heading font-medium text-[#5C5C55]">{s.studios_vacant}</p>
              <p className="text-[10px] text-[#5C5C55]">Vacant</p>
            </div>
          </div>
        </section>
      </div>

      {/* ── SITE OPERATIONS SUMMARY ── */}
      {(() => {
        const ops = data?.site_ops_summary || {};
        const hasIssues = ops.missed > 0 || ops.overcharge_alerts > 0;
        const total = (ops.missed || 0) + (ops.pending || 0) + (ops.overcharge_alerts || 0);
        if (total === 0) return null;
        return (
          <section className="mt-6 bg-white rounded-2xl border border-[#E5E2DC] shadow-[0_2px_10px_rgba(0,0,0,0.02)] p-6" data-testid="site-ops-summary">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Wrench className="w-4 h-4 text-[#3A4035]" strokeWidth={1.5} />
                <h2 className="font-heading text-base font-medium text-[#1A1A1A]">Site Operations</h2>
              </div>
              <button onClick={() => navigate('/site-ops')} className="text-xs text-[#5C5C55] hover:text-[#1A1A1A] font-medium flex items-center gap-1" data-testid="view-site-ops-link">
                View all <ChevronRight className="w-3 h-3" strokeWidth={1.5} />
              </button>
            </div>
            <div className="space-y-2">
              {ops.missed > 0 && (
                <button onClick={() => navigate('/site-ops')} className="w-full flex items-center gap-3 p-3 rounded-xl bg-[#FDF2F2] border border-[#F0D4D4] text-left hover:shadow-sm transition-shadow" data-testid="ops-missed">
                  <X className="w-4 h-4 text-[#B33A3A] shrink-0" strokeWidth={1.5} />
                  <p className="text-sm text-[#B33A3A] font-medium">{ops.missed} missed service{ops.missed > 1 ? 's' : ''} this week</p>
                  <ChevronRight className="w-3 h-3 text-[#B33A3A] ml-auto shrink-0" strokeWidth={1.5} />
                </button>
              )}
              {ops.pending > 0 && (
                <button onClick={() => navigate('/site-ops')} className="w-full flex items-center gap-3 p-3 rounded-xl bg-[#FDFAF0] border border-[#EDE4C8] text-left hover:shadow-sm transition-shadow" data-testid="ops-pending">
                  <CalendarClock className="w-4 h-4 text-[#8A7230] shrink-0" strokeWidth={1.5} />
                  <p className="text-sm text-[#8A7230] font-medium">{ops.pending} service{ops.pending > 1 ? 's' : ''} pending this week</p>
                  <ChevronRight className="w-3 h-3 text-[#8A7230] ml-auto shrink-0" strokeWidth={1.5} />
                </button>
              )}
              {ops.overcharge_alerts > 0 && (
                <button onClick={() => navigate('/site-ops')} className="w-full flex items-center gap-3 p-3 rounded-xl bg-[#FDF2F2] border border-[#F0D4D4] text-left hover:shadow-sm transition-shadow" data-testid="ops-overcharge">
                  <AlertTriangle className="w-4 h-4 text-[#B33A3A] shrink-0" strokeWidth={1.5} />
                  <p className="text-sm text-[#B33A3A] font-medium">{ops.overcharge_alerts} invoice variance{ops.overcharge_alerts > 1 ? 's' : ''} flagged</p>
                  <ChevronRight className="w-3 h-3 text-[#B33A3A] ml-auto shrink-0" strokeWidth={1.5} />
                </button>
              )}
            </div>
          </section>
        );
      })()}

      {/* ── LOCATION DEMAND SIGNALS ── */}
      {(data?.location_demand || []).length > 0 && (
        <section className="mt-6 bg-white rounded-2xl border border-[#E5E2DC] shadow-[0_2px_10px_rgba(0,0,0,0.02)] p-6" data-testid="location-demand-section">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-[#3A4035]" strokeWidth={1.5} />
              <h2 className="font-heading text-base font-medium text-[#1A1A1A]">Location Demand</h2>
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#F2F0EB] text-[#8A8B85] font-medium">{(data?.location_demand || []).reduce((s, d) => s + d.count, 0)} total</span>
            </div>
            <p className="text-[10px] text-[#8A8B85]">From website interest registrations</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {(data?.location_demand || []).map((d, i) => (
              <div key={d.location} className={`flex items-center gap-3 px-4 py-3.5 rounded-xl border transition-colors ${i === 0 ? 'bg-[#F0F2EF] border-[#D8DBD3]' : 'bg-[#FAF9F6] border-[#F2F0EB] hover:border-[#E5E2DC]'}`}
                data-testid={`demand-${d.location.toLowerCase().replace(/\s/g, '-')}`}>
                <p className="flex-1 text-sm font-medium text-[#1A1A1A]">{d.location}</p>
                <div className="text-right shrink-0">
                  <p className="text-lg font-heading font-medium text-[#3A4035]">{d.count}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Recent submissions */}
          {(data?.recent_interests || []).length > 0 && (
            <div className="mt-5 pt-4 border-t border-[#F2F0EB]">
              <p className="text-[10px] tracking-[0.1em] uppercase font-semibold text-[#8A8B85] mb-3">Recent Submissions</p>
              <div className="space-y-1">
                {(data?.recent_interests || []).map((interest, i) => (
                  <div key={interest.interest_id || i} className="flex items-center gap-3 py-2 px-2 -mx-2 rounded-lg">
                    <div className="w-6 h-6 rounded-full bg-[#F2F0EB] flex items-center justify-center text-[9px] font-semibold text-[#5C5C55] shrink-0">
                      {(interest.name || '?').charAt(0)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-[#1A1A1A] truncate">{interest.name}</p>
                    </div>
                    <p className="text-[10px] text-[#8A8B85] shrink-0">{interest.preferred_location}</p>
                    <p className="text-[10px] text-[#8A8B85] shrink-0">{interest.business_type}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>
      )}
    </div>
  );
}
